/**
 * Regelversion 8: Auch Unternehmen und Arbeitnehmer sind nur begrenzt belastbar.
 *
 * Wie bei den Vermögenden (Version 7, engine/rich.ts) zählt jedes Gesetz, das Unternehmen oder
 * Arbeitnehmer zur Kasse bittet, zu ihrer Belastung (Mrd €/Jahr). Entlastungen senken sie.
 *  - Abnehmender Ertrag: Über einer Schwelle kommt pro Euro immer weniger an.
 *  - Abwanderung: Unternehmen verlagern Standorte (weniger Wachstum, Loch im Haushalt),
 *    Fachkräfte wandern aus (Fachkräftemangel wächst, Loch im Haushalt).
 *  - Ein Standortpaket holt Abgewanderte nach und nach zurück.
 * Alles hier greift nur, wenn `since(8)` gilt.
 */
import { OUTLETS } from '../content/parties';
import { S, log } from './state';
import { clamp, de } from './util';
import { since } from './version';
import { press7 } from './news7';

export type BG = 'unt' | 'arb';
export const CAPS: Record<BG, { n: string; full: number; zero: number; flight: number; k: number; max: number }> = {
  unt: { n: 'Unternehmen', full: 20, zero: 60, flight: 25, k: .006, max: .4 },
  arb: { n: 'Arbeitnehmer', full: 25, zero: 70, flight: 30, k: .005, max: .35 },
};
export const b8On = (): boolean => since(8);

const marg = (g: BG, x: number) => { const c = CAPS[g]; return x <= c.full ? 1 : x >= c.zero ? .1 : 1 - .9 * (x - c.full) / (c.zero - c.full); };
export const margOf = marg;
function yieldOf(g: BG, l0: number, r: number) { if (r <= 0) return 1; let s = 0; for (let i = 0; i < 20; i++) s += marg(g, l0 + r * (i + .5) / 20); return s / 20; }

/** Belastung eines Entwurfs je Gruppe (mit Vorzeichen). */
export function burdenOf(T: any, p: any, fx: any): Record<BG, number> {
  const o: Record<BG, number> = { unt: 0, arb: 0 };
  if (fx?.fin?.opt === 1) o.unt += (fx.fin.c || 0) * .6; // Subventionen streichen
  if (T?.custom) for (const B of (p?.blocks || [])) if (B.k === 'tax') { const w = B.who || 0, v = +B.v || 0; if (w === 0) o.unt += v; if (w === 1) o.arb += v * .5; }
  if (fx?.b8) { o.unt += fx.b8.unt || 0; o.arb += fx.b8.arb || 0; }
  o.unt = Math.round(o.unt * 10) / 10; o.arb = Math.round(o.arb * 10) / 10;
  return o;
}
const counts = (L: any) => !L.rep && !['failed', 'blocked', 'repealed'].includes(L.stage);
export function loadOf(g: BG, except?: number | null, enactedOnly = false): number {
  return Math.max(0, (S.laws || []).filter((L: any) => L.id !== except && counts(L) && (!enactedOnly || ['kraft', 'done'].includes(L.stage)))
    .reduce((s: number, L: any) => s + (L.fx?.[g + '8'] || 0), 0));
}
/** Stärke der Rückholung durch Standortpakete in Kraft (0–1). */
export function lureOf(g: 'rich' | BG): number {
  if (!since(8)) return 0;
  return clamp((S.laws || []).filter((L: any) => !L.rep && ['kraft', 'done'].includes(L.stage)).reduce((s: number, L: any) => s + (L.fx?.lure?.[g] || 0), 0), 0, 1);
}

/** Wirkung eines Entwurfs nach abnehmendem Ertrag. Aufruf aus lawFx nach applyRich. */
export function applyBurden(T: any, p: any, fx: any, except?: number | null): any {
  if (!b8On()) return fx;
  const o = burdenOf(T, p, fx); let out = fx;
  for (const g of ['unt', 'arb'] as BG[]) {
    const r = o[g]; if (!r) continue;
    const y = r > 0 ? yieldOf(g, loadOf(g, except), r) : 1;
    out = { ...out, b: (out.b || 0) - (r > 0 ? r * (1 - y) : 0), [g + '8']: r, [g + 'Y']: Math.round(y * 100) / 100 };
  }
  return out;
}

const FL: Record<BG, 'flightU' | 'flightA'> = { unt: 'flightU', arb: 'flightA' };
/** Monatlich: Abwanderung von Unternehmen und Fachkräften. */
export function burdenTick() {
  if (!b8On() || S.phase !== 'govern') return;
  for (const g of ['unt', 'arb'] as BG[]) {
    const c = CAPS[g], k = FL[g], load = loadOf(g, null, true), before = (S as any)[k] || 0;
    let f = load > c.flight ? Math.min(before + (load - c.flight) * c.k, (load - c.flight) * c.max) : before * .97;
    f *= 1 - .05 * lureOf(g);
    (S as any)[k] = f;
    for (const th of [1, 3, 6]) if (before < th && f >= th) {
      if (g === 'unt') { log(`Standortflucht: Unternehmen verlagern Produktion und Zentralen ins Ausland. Dem Haushalt fehlen ${de(f, 1)} Mrd € pro Jahr, das Wachstum leidet.`, 'bad'); press7(OUTLETS[1].n, th >= 6 ? 'Deindustrialisierung: Konzerne kehren Deutschland den Rücken' : 'Mittelständler denken über Verlagerung nach', th >= 6 ? -1.2 : -.6); }
      else { log(`Auswanderung: Gut ausgebildete Arbeitnehmer zieht es ins Ausland. Dem Haushalt fehlen ${de(f, 1)} Mrd € pro Jahr, der Fachkräftemangel wächst.`, 'bad'); press7(OUTLETS[0].n, th >= 6 ? 'Brain Drain: Rekord bei Auswanderung von Fachkräften' : 'Immer mehr Fachkräfte verlassen Deutschland', th >= 6 ? -1.2 : -.6); }
    }
  }
  if (S.issues?.fach && S.issues.fach.on) S.issues.fach.sev = clamp(S.issues.fach.sev + (S.flightA || 0) * .04, 0, 100);
}
/** Kosten im Haushalt (Mrd €/Jahr). */
export const flightCost8 = (): number => (b8On() ? clamp((S.flightU || 0) + (S.flightA || 0), 0, 40) : 0);
/** Bremse fürs Wachstum (Prozentpunkte). */
export const flightGr8 = (): number => (b8On() ? (S.flightU || 0) * .03 + (S.flightA || 0) * .02 : 0);
